import environment from '@/lib/environment';
import { ApiError } from '@/utils/errors/ApiError';
import { logError } from '@/utils/errors/ErrorLogger';
import { getHolderDisplay } from '@/utils/displayUtils';
import { toTimeoutApiError } from '@/utils/authBootstrapErrors';
import { isAbortOrTimeoutError, withRequestTimeout } from '@/utils/requestTimeout';
import {
  safeGetItem,
  safeRemoveItem,
  safeSessionGetItem,
  safeSessionRemoveItem,
  safeSessionSetItem,
  safeSetItem,
} from '@/utils/safeStorage';
import { AUTH } from '@/constants';
import { parseRetryAfterMsFromResponse } from '@/utils/rateLimit';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface MembershipSummary {
  companyId: number;
  companyName: string;
  roleId: number;
  roleName: string;
}

export interface InvitationPreview {
  email: string;
  companyName: string;
  roleName: string;
  alreadyRegistered: boolean;
  isExternalServiceProvider?: boolean;
  expiresAt?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  memberships: MembershipSummary[];
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    roleId?: string;
    companyId?: string;
  };
}

/** Response from POST /users/login when the user has email MFA enabled. */
export interface LoginMfaRequiredResponse {
  mfaRequired: true;
  mfaToken: string;
  methods?: Array<'email' | 'totp' | 'recovery'>;
  preferred?: 'email' | 'totp';
}

export function isLoginMfaRequired(r: LoginResponse | LoginMfaRequiredResponse): r is LoginMfaRequiredResponse {
  return 'mfaRequired' in r && (r as LoginMfaRequiredResponse).mfaRequired === true;
}

export interface ResetPasswordRequest {
  email?: string;
  password?: string;
  code?: string;
}

/**
 * Base API service class containing common request logic, authentication, and utilities
 */
export class BaseApiService {
  protected readonly baseUrl: string;
  private onAuthFailureCallback: (() => void) | null = null;
  private refreshPromise: Promise<void> | null = null;
  private refreshDisabled: boolean = false;
  private sessionPermissions: string[] = [];
  private static readonly REFRESH_DISABLED_KEY = 'xkey-refresh-disabled';
  private static readonly CSRF_CLIENT_HEADER = 'X-Xkey-Client';
  private static readonly CSRF_CLIENT_VALUE = 'xkey-web';

  constructor() {
    this.baseUrl = environment.getServerUrl();
    // If a refresh token cookie is HttpOnly, JS cannot clear it on logout. When refresh fails once,
    // we disable further refresh attempts for this tab/session to avoid 401/refresh loops.
    this.refreshDisabled = safeSessionGetItem(BaseApiService.REFRESH_DISABLED_KEY) === '1';
  }

  // Expose baseUrl for file uploads/downloads
  getBaseUrl(): string {
    return this.baseUrl;
  }

  setAuthFailureCallback(callback: () => void) {
    this.onAuthFailureCallback = callback;
  }

  protected invalidateAuthCache() {
    if (this.onAuthFailureCallback) {
      this.onAuthFailureCallback();
    }
  }

  public async request<T>(
    endpoint: string,
    options: RequestInit & { timeoutMs?: number } = {},
    isRetryAfterRefresh: boolean = false,
    isRetryAfterRateLimit: boolean = false,
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const { timeoutMs, ...fetchOptions } = options;

    const config: RequestInit = {
      ...fetchOptions,
      credentials: 'include',
      signal: withRequestTimeout(fetchOptions.signal, timeoutMs),
      headers: {
        'Content-Type': 'application/json',
        [BaseApiService.CSRF_CLIENT_HEADER]: BaseApiService.CSRF_CLIENT_VALUE,
        ...fetchOptions.headers,
      },
    };

    const token = this.getToken();
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    // Add selected company header for Super_Admin users
    const selectedCompany = this.getSelectedCompany();
    if (selectedCompany) {
      config.headers = {
        ...config.headers,
        'X-Selected-Company-Id': selectedCompany.id.toString(),
      };
    }

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        const isAuthHandshakeEndpoint =
          endpoint.startsWith('users/login') ||
          endpoint.startsWith('users/register') ||
          endpoint.startsWith('users/reset-password') ||
          endpoint.endsWith('/refresh');

        if (response.status === 401 && !isAuthHandshakeEndpoint && !isRetryAfterRefresh && this.canAttemptRefresh()) {
          try {
            await this.attemptTokenRefresh();
            // Retry the original request with new token
            return this.request<T>(endpoint, options, true);
          } catch {
            // Refresh failed, logout and let React app show login screen
            this.clearUserInfo();
            // Trigger auth state update by invalidating React Query cache
            this.invalidateAuthCache();
            throw new Error('Session expired. Please login again.');
          }
        }

        // If we get 401 but refresh is disabled/unavailable, force a single logout so the UI
        // stops issuing authenticated queries (e.g. user settings) and shows the login shell.
        if (response.status === 401 && !isAuthHandshakeEndpoint && !isRetryAfterRefresh && !this.canAttemptRefresh()) {
          this.clearUserInfo();
          this.invalidateAuthCache();
          throw new Error('Session expired. Please login again.');
        }

        if (response.status === 403 && endpoint.endsWith('/refresh')) {
          this.clearUserInfo();
          // Trigger auth state update by invalidating React Query cache
          this.invalidateAuthCache();
          throw new Error('Session expired. Please login again.');
        }

        const message = Array.isArray(errorData.message)
          ? errorData.message.join('; ')
          : errorData.message || `HTTP error! status: ${response.status}`;

        if (response.status === 429 && !isAuthHandshakeEndpoint && !isRetryAfterRateLimit) {
          const waitMs = parseRetryAfterMsFromResponse(response, message) + Math.floor(Math.random() * 250);
          await new Promise((resolve) => {
            setTimeout(resolve, waitMs);
          });
          return this.request<T>(endpoint, options, isRetryAfterRefresh, true);
        }

        throw new ApiError(message, response.status, Array.isArray(errorData.errors) ? errorData.errors : undefined);
      }

      const contentLength = response.headers.get('content-length');
      const contentType = response.headers.get('content-type');

      if (contentLength === '0' || !contentType?.includes('application/json')) {
        return undefined as T;
      }

      const text = await response.text();
      if (!text) {
        return undefined as T;
      }

      return JSON.parse(text);
    } catch (e) {
      if (isAbortOrTimeoutError(e)) {
        const timeoutError = toTimeoutApiError(e);
        logError('API request timed out', timeoutError, { component: 'BaseApiService', action: 'request' });
        throw timeoutError;
      }
      logError('API request failed', e as Error, { component: 'BaseApiService', action: 'request' });
      throw e;
    }
  }

  private async attemptTokenRefresh(): Promise<void> {
    // Prevent concurrent refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    try {
      await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(): Promise<void> {
    try {
      const response = await this.refresh();
      await this.setUserInfo(response);
      this.clearRefreshDisabled();
    } catch (error) {
      this.disableRefresh();
      logError('Token refresh failed during API call', error as Error, {
        component: 'BaseApiService',
        action: 'performTokenRefresh',
      });
      throw error;
    }
  }

  async login(credentials: LoginCredentials): Promise<LoginResponse | LoginMfaRequiredResponse> {
    const url = `${this.baseUrl}users/login`;

    // Your LocalStrategy expects 'email' field based on usernameField: 'email'
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        [BaseApiService.CSRF_CLIENT_HEADER]: BaseApiService.CSRF_CLIENT_VALUE,
      },
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      logError('Login failed', new Error(`HTTP ${response.status}`), {
        component: 'BaseApiService',
        action: 'login',
        metadata: { status: response.status, statusText: response.statusText, error: errorData },
      });
      throw new Error(errorData.message || `Login failed: ${response.status} ${response.statusText}`);
    }

    const result = (await response.json()) as LoginResponse | LoginMfaRequiredResponse;
    return result;
  }

  async verifyMfaLogin(payload: {
    mfaToken: string;
    code: string;
    method?: 'email' | 'totp' | 'recovery';
  }): Promise<LoginResponse> {
    return this.request<LoginResponse>('users/login/mfa', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async totpSetup(): Promise<{ issuer: string; accountName: string; otpauthUrl: string; secret?: string }> {
    return this.request('users/mfa/totp/setup', { method: 'POST' });
  }

  async totpEnable(code: string): Promise<{ codes: string[] }> {
    return this.request('users/mfa/totp/enable', { method: 'POST', body: JSON.stringify({ code }) });
  }

  async totpDisable(): Promise<void> {
    return this.request('users/mfa/totp/disable', { method: 'POST' });
  }

  async recoveryRegenerate(): Promise<{ codes: string[] }> {
    return this.request('users/mfa/recovery/regenerate', { method: 'POST' });
  }

  async resendMfaCode(mfaToken: string): Promise<LoginMfaRequiredResponse> {
    return this.request<LoginMfaRequiredResponse>('users/login/mfa/resend', {
      method: 'POST',
      body: JSON.stringify({ mfaToken }),
    });
  }

  async refresh(refreshToken?: string, token?: string): Promise<LoginResponse> {
    return this.request<LoginResponse>('users/refresh', {
      method: 'POST',
      body: JSON.stringify(refreshToken && token ? { refreshToken, token } : {}),
    });
  }

  async logout(): Promise<void> {
    return this.request<void>('users/logout', {
      method: 'POST',
    });
  }

  async register(user: Record<string, unknown>, invitationCode: string): Promise<unknown> {
    return this.request(`users/register/${invitationCode}`, {
      method: 'POST',
      body: JSON.stringify(user),
    });
  }

  async previewInvitation(invitationCode: string): Promise<InvitationPreview> {
    return this.request<InvitationPreview>(`users/register/${invitationCode}`, { method: 'GET' });
  }

  async acceptInvitation(invitationCode: string): Promise<{ memberships: MembershipSummary[] }> {
    return this.request<{ memberships: MembershipSummary[] }>(`invitations/accept/${invitationCode}`, {
      method: 'POST',
    });
  }

  // Password reset methods
  async getResetPassword(code: string): Promise<unknown> {
    return this.request(`users/reset-password/${code}`);
  }

  async resetPassword(values: ResetPasswordRequest): Promise<unknown> {
    return this.request('users/reset-password', {
      method: 'POST',
      body: JSON.stringify(values),
    });
  }

  async getUserPermissions(roleId: string): Promise<string[]> {
    return this.request<string[]>(`roles/${roleId}/permissions`);
  }

  getToken(): string | null {
    return safeSessionGetItem(AUTH.SESSION_ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return null;
  }

  setSessionPermissions(permissions: string[]): void {
    this.sessionPermissions = permissions;
  }

  private removeCookie(name: string): void {
    const isSecure = window.location.protocol === 'https:';
    const secureFlag = isSecure ? ';Secure' : '';
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Strict${secureFlag}`;
  }

  // Selected company management (for Super_Admin users)
  getSelectedCompany(): { id: number } | null {
    const savedCompany = safeGetItem(AUTH.SELECTED_COMPANY_KEY);
    if (savedCompany) {
      try {
        return JSON.parse(savedCompany);
      } catch {
        safeRemoveItem(AUTH.SELECTED_COMPANY_KEY);
      }
    }
    return null;
  }

  // User info management
  async setUserInfo(data: LoginResponse): Promise<void> {
    // Persist tokens from the response body so requests can send Authorization Bearer when cross-origin
    // cookies are missing or not attached (common WebKit vs Chrome differences on SPA + separate API host).
    if (data.token) {
      safeSessionSetItem(AUTH.SESSION_ACCESS_TOKEN_KEY, data.token);
    }
    safeSessionRemoveItem(AUTH.SESSION_REFRESH_TOKEN_KEY);
    this.clearRefreshDisabled();

    safeSetItem('fullName', getHolderDisplay(data.user));
    safeSetItem('userId', data.user.id);

    // Auto-select the first membership when none is explicitly stored yet (single-company users).
    // Multi-company users get prompted by SelectedCompanyContext to pick, then header is sent.
    const memberships = data.memberships ?? [];
    const storedSelection = safeGetItem(AUTH.SELECTED_COMPANY_KEY);
    if (!storedSelection && memberships.length > 0) {
      const first = memberships[0];
      safeSetItem(AUTH.SELECTED_COMPANY_KEY, JSON.stringify({ id: first.companyId, name: first.companyName }));
    }

    const selectedCompanyId = (() => {
      const raw = safeGetItem(AUTH.SELECTED_COMPANY_KEY);
      if (!raw) return null;
      try {
        const parsed = JSON.parse(raw) as { id?: unknown };
        return typeof parsed?.id === 'number' ? parsed.id : null;
      } catch {
        return null;
      }
    })();

    const activeMembership =
      (selectedCompanyId != null ? memberships.find((m) => m.companyId === selectedCompanyId) : undefined) ??
      memberships[0];

    safeSetItem('companyId', activeMembership?.companyId?.toString() ?? data.user.companyId ?? '');

    try {
      const activeRoleId = activeMembership?.roleId?.toString() ?? data.user.roleId;
      if (activeRoleId) {
        const permissions = await this.getUserPermissions(activeRoleId);
        this.setSessionPermissions(permissions);
      } else {
        this.setSessionPermissions([]);
      }
    } catch (e) {
      logError('Failed to fetch user permissions', e as Error, {
        component: 'BaseApiService',
        action: 'fetchPermissions',
      });
    }
  }

  clearUserInfo(): void {
    this.removeCookie('token');
    this.removeCookie('refreshToken');
    safeSessionRemoveItem(AUTH.SESSION_ACCESS_TOKEN_KEY);
    safeSessionRemoveItem(AUTH.SESSION_REFRESH_TOKEN_KEY);
    this.sessionPermissions = [];
    safeRemoveItem('permissions');
    safeRemoveItem('fullName');
    safeRemoveItem('userId');
    safeRemoveItem('companyId');
    safeRemoveItem(AUTH.SELECTED_COMPANY_KEY);
  }

  private canAttemptRefresh(): boolean {
    if (this.refreshDisabled) return false;
    // Refresh tokens may be stored in HttpOnly cookies (not readable by JS), so we can't reliably
    // detect an "auth session" before trying. If refresh fails once, we disable further attempts
    // for this tab/session to avoid 401/refresh loops.
    return true;
  }

  private disableRefresh(): void {
    this.refreshDisabled = true;
    safeSessionSetItem(BaseApiService.REFRESH_DISABLED_KEY, '1');
  }

  private clearRefreshDisabled(): void {
    this.refreshDisabled = false;
    safeSessionRemoveItem(BaseApiService.REFRESH_DISABLED_KEY);
  }

  // User utility methods
  getCurrentUserId(): number | null {
    const userId = safeGetItem('userId');
    return userId ? parseInt(userId, 10) : null;
  }

  getCompanyId(): number | null {
    const companyId = safeGetItem('companyId');
    return companyId ? parseInt(companyId, 10) : null;
  }

  userHasPermissions(...permissions: string[]): boolean {
    if (this.sessionPermissions.length === 0) return false;
    const permissionSet = new Set(this.sessionPermissions);
    return permissions.every((p) => permissionSet.has(p));
  }

  userHasSomeOfThePermissions(...permissions: string[]): boolean {
    if (this.sessionPermissions.length === 0) return false;
    const permissionSet = new Set(this.sessionPermissions);
    return permissions.some((p) => permissionSet.has(p));
  }

  userHasRightCompany(companyId: number): boolean {
    const userCompanyId = safeGetItem('companyId');
    return userCompanyId ? userCompanyId === companyId.toString() : false;
  }

  isLoggedIn(): boolean {
    // Tokens are stored in HttpOnly cookies (not readable by JS).
    // Treat presence of minimal persisted user info as "logged in"; real auth is validated by `/users/me` calls.
    return !!safeGetItem('userId');
  }

  async getVersion(): Promise<{ version: string }> {
    return this.request<{ version: string }>('version');
  }
}
